<script setup>
import { ref, watch, computed, defineProps, defineEmits } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import ToggleSwitch from 'primevue/toggleswitch';
import Message from 'primevue/message';
import { useToast } from 'primevue/usetoast';
import { adminAPI } from '../service/api.js';
import { useCurrentUser } from '../composables/useCurrentUser';

const props = defineProps({
  visible: { type: Boolean, required: true },
  user: { type: Object, default: null },      // {username, roles[]}
  knownRoles: { type: Array, default: () => [] },
});
const emit = defineEmits(['update:visible', 'changed']);

const toast = useToast();
const { username: currentUsername } = useCurrentUser();

// Local working copy of the user's role set so toggle interactions feel
// instant; we push each change to the API independently.
const localRoles = ref(new Set());
const busy = ref(new Set()); // role names currently being grant/revoked

watch(() => props.visible, (now) => {
  if (now && props.user) {
    localRoles.value = new Set(props.user.roles || []);
    busy.value = new Set();
  }
});

const isSelf = computed(() =>
  props.user?.username?.toLowerCase() === currentUsername.value.toLowerCase()
);

// UI hint matching the backend's lockout guard: a role_admin cannot revoke
// their own role_admin role. Backend enforces with 409 — this just stops the
// click before it happens.
const isLockedToggle = (role) =>
  isSelf.value && role === 'role_admin' && localRoles.value.has('role_admin');

const toggle = async (role, newValue) => {
  if (busy.value.has(role)) return;
  if (isLockedToggle(role) && newValue === false) {
    toast.add({
      severity: 'warn',
      summary: 'Cannot revoke',
      detail: 'You cannot revoke role_admin from yourself.',
      life: 4000,
    });
    return;
  }

  busy.value.add(role);
  try {
    if (newValue) {
      await adminAPI.grantRole(props.user.username, role);
      localRoles.value.add(role);
      toast.add({
        severity: 'success',
        summary: 'Role granted',
        detail: `${role} → ${props.user.username}`,
        life: 2500,
      });
    } else {
      await adminAPI.revokeRole(props.user.username, role);
      localRoles.value.delete(role);
      toast.add({
        severity: 'success',
        summary: 'Role revoked',
        detail: `${role} from ${props.user.username}`,
        life: 2500,
      });
    }
    emit('changed');
  } catch (e) {
    console.error('toggle role failed:', e);
    // Force-reset the toggle visual state — the watcher on `localRoles`
    // re-renders the ToggleSwitch from this value.
    localRoles.value = new Set(localRoles.value);
    toast.add({
      severity: 'error',
      summary: newValue ? 'Failed to grant role' : 'Failed to revoke role',
      detail: e?.response?.data || e?.message || 'Unknown error',
      life: 5000,
    });
  } finally {
    busy.value.delete(role);
    busy.value = new Set(busy.value);
  }
};

const close = () => {
  emit('update:visible', false);
};
</script>

<template>
  <Dialog
    :visible="visible"
    @update:visible="(v) => emit('update:visible', v)"
    modal
    :style="{ width: '28rem' }"
    :header="user ? `Roles for ${user.username}` : 'Roles'"
  >
    <div v-if="user" class="dialog-body">
      <Message v-if="isSelf" severity="info" :closable="false" class="self-banner">
        You are editing your own roles. The toggle for <strong>role_admin</strong> is
        locked to prevent accidental self-lockout — use the CLI if you need to remove it.
      </Message>

      <div v-for="role in knownRoles" :key="role" class="role-row">
        <ToggleSwitch
          :model-value="localRoles.has(role)"
          @update:model-value="(v) => toggle(role, v)"
          :disabled="busy.has(role) || isLockedToggle(role)"
          :aria-label="`Toggle ${role}`"
        />
        <div class="role-meta">
          <code class="role-name">{{ role }}</code>
          <i v-if="busy.has(role)" class="pi pi-spinner pi-spin busy-icon" />
        </div>
      </div>

      <div class="dialog-actions">
        <Button label="Done" @click="close" />
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
.dialog-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.self-banner {
  margin: 0;
}
.role-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0.25rem;
  border-bottom: 1px solid #f0f0f0;
}
.role-row:last-of-type {
  border-bottom: none;
}
.role-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-align: left;
  flex: 1;
}
.role-name {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.9rem;
}
.busy-icon {
  color: #888;
  font-size: 0.85rem;
}
.dialog-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
}
</style>
